import * as React from 'react';

import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  DEMO_DIMENSIONS,
  DIMENSION_LABEL,
  demoGrid,
  type Demographic,
} from './demographic';

/**
 * The demographic grid a translation will actually be rendered over.
 *
 * The pickers above describe four marginals; this is their cross product, which
 * is the thing `build_demo_grid` builds and `render_ggvar` averages over. Two
 * translations can name the same countries and mean different populations, and
 * the marginals alone do not show that — the joint weights do.
 *
 * Behind a disclosure because it is a check, not a step: the weights are
 * already stated per dimension, and the grid is what they multiply out to.
 */

/** Enough rows to audit a grid; past this, scrolling is not reading. */
const MAX_ROWS = 500;

const DemographicGrid: React.FC<{ demographic: Demographic | null }> = ({
  demographic,
}) => {
  const grid = React.useMemo(() => demoGrid(demographic), [demographic]);

  if (!grid.length) {
    return (
      <Alert severity="info">
        The grid appears once every dimension names at least one level.
      </Alert>
    );
  }

  const total = grid.reduce((sum, cell) => sum + cell.weight, 0);
  const shown = grid.slice(0, MAX_ROWS);

  return (
    <>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {grid.length} {grid.length === 1 ? 'cell' : 'cells'}, weights summing to{' '}
        {total.toFixed(3)}. Each weight is the product of its four marginals.
      </Typography>

      {grid.length > MAX_ROWS && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary' }}
        >
          Showing the first {MAX_ROWS}.
        </Typography>
      )}

      <Box sx={{ maxHeight: 320, overflow: 'auto', mt: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {DEMO_DIMENSIONS.map((d) => (
                <TableCell key={d}>{DIMENSION_LABEL[d]}</TableCell>
              ))}
              <TableCell align="right">Weight</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((cell) => (
              <TableRow
                key={DEMO_DIMENSIONS.map((d) => cell[d]).join('|')}
                hover
              >
                {DEMO_DIMENSIONS.map((d) => (
                  <TableCell key={d}>{cell[d]}</TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {(cell.weight * 100).toFixed(2)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

const AdvancedGrid: React.FC<{ demographic: Demographic | null }> = ({
  demographic,
}) => (
  <Accordion
    disableGutters
    elevation={0}
    // Collapsed children stay mounted by default, so every drag frame would
    // re-render the whole grid — hundreds of rows nobody is looking at.
    TransitionProps={{ unmountOnExit: true }}
    sx={{ border: '1px solid #e0e0e0' }}
  >
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant="body2">Advanced — show the grid</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <DemographicGrid demographic={demographic} />
    </AccordionDetails>
  </Accordion>
);

export default AdvancedGrid;
